import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ScanLine, Mail, Lock, User, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";

function formatErr(detail) {
  if (!detail) return "Une erreur est survenue";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
  return detail?.msg || String(detail);
}

function describeAxiosError(err) {
  // Network error (server unreachable, CORS, DNS): err.response is undefined
  if (!err?.response) {
    return "Connexion au serveur impossible. Vérifiez votre internet et réessayez. Si le problème persiste, le serveur redémarre — patientez 30 secondes.";
  }
  const status = err.response.status;
  const detail = err.response.data?.detail;
  if (detail) return formatErr(detail);
  if (status === 429) return "Trop de tentatives de connexion. Réessayez dans 15 minutes.";
  if (status >= 500) return `Erreur serveur (${status}). Réessayez dans quelques secondes.`;
  return `Erreur ${status}`;
}

function safeLocalStorageSet(key, val) {
  try { localStorage.setItem(key, val); return true; } catch { return false; }
}

export default function Login() {
  const nav = useNavigate();
  const { checkAuth } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "register" | "forgot"
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [authError, setAuthError] = useState(null);
  const emailRef = useRef(null);
  const nameRef = useRef(null);
  const passwordRef = useRef(null);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setAuthError(null);
    // Read from refs as fallback (mobile autofill doesn't always fire onChange)
    const emailVal = (emailRef.current?.value || email || "").trim().toLowerCase();
    const nameVal = (nameRef.current?.value || name || "").trim();
    const pwVal = passwordRef.current?.value || password || "";

    if (!emailVal) { setAuthError("Veuillez entrer votre courriel"); return; }
    if (mode !== "forgot" && pwVal.length < 8) {
      setAuthError("Le mot de passe doit faire au moins 8 caractères");
      return;
    }
    if (mode === "register" && !nameVal) { setAuthError("Veuillez entrer votre nom"); return; }

    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: emailVal, password: pwVal });
        const stored = data?.session_token ? safeLocalStorageSet("session_token", data.session_token) : false;
        if (data?.session_token && !stored) {
          setAuthError("Votre navigateur bloque le stockage local. Désactivez le mode Privé puis réessayez.");
          setLoading(false);
          return;
        }
        toast.success("Bienvenue !");
        await checkAuth();
        nav("/dashboard");
      } else if (mode === "register") {
        const { data } = await api.post("/auth/register", { email: emailVal, name: nameVal, password: pwVal });
        const stored = data?.session_token ? safeLocalStorageSet("session_token", data.session_token) : false;
        if (data?.session_token && !stored) {
          setAuthError("Votre navigateur bloque le stockage local. Désactivez le mode Privé puis réessayez.");
          setLoading(false);
          return;
        }
        toast.success("Compte créé !");
        await checkAuth();
        nav("/dashboard");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: emailVal });
        setForgotSent(true);
      }
    } catch (err) {
      const msg = describeAxiosError(err);
      setAuthError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const bg = "https://images.unsplash.com/photo-1772001936267-b6058748eff4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwaW50ZXJpb3IlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc3NjM2NTg1Nnww&ixlib=rb-4.1.0&q=85";

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col p-6 md:p-12 lg:p-16 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center">
            <ScanLine className="text-white" size={18} />
          </div>
          <span className="font-heading font-black text-xl tracking-tighter">FACTURÉ</span>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-xl py-8">
          <p className="overline mb-4">Compte de dépenses · Swiss mode</p>
          <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-none text-[#0B0F19]">
            {mode === "register" ? <>Créez votre<br />compte.</> :
             mode === "forgot" ? <>Mot de passe<br />oublié ?</> :
             <>Scannez, triez,<br />exportez <span className="text-[#002FA7]">sans effort.</span></>}
          </h1>

          <div className="mt-8 max-w-md">
            {/* Google OAuth */}
            <button
              type="button"
              data-testid="google-login-button"
              onClick={handleGoogle}
              className="btn-secondary justify-center w-full py-3 mb-4"
            >
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.6-5.6C33.9 6.3 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.8 19 13 24 13c3 0 5.8 1.1 8 3l5.6-5.6C33.9 6.3 29.2 4 24 4 16.2 4 9.4 8.4 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.1 0 9.8-2 13.3-5.2l-6.2-5.1C29 35 26.6 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.4 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.2 5.1C40.7 36 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z"/>
              </svg>
              Continuer avec Google
            </button>

            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">ou par courriel</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {forgotSent && mode === "forgot" ? (
              <div className="ra-card p-5 text-center" data-testid="forgot-sent">
                <div className="font-heading font-black text-lg mb-2">Courriel envoyé</div>
                <p className="text-sm text-gray-600">Si un compte existe avec ce courriel, vous recevrez un lien pour réinitialiser votre mot de passe dans quelques instants.</p>
                <button type="button" className="btn-ghost mt-4" onClick={() => { setMode("login"); setForgotSent(false); }}>
                  Retour à la connexion
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="auth-form" noValidate>
                {authError && (
                  <div className="rounded border-l-4 border-red-500 bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-800 dark:text-red-200" data-testid="auth-error-banner">
                    {authError}
                  </div>
                )}
                {mode === "register" && (
                  <div>
                    <label className="ra-label"><User size={12} className="inline mr-1" />Nom complet</label>
                    <input
                      ref={nameRef}
                      className="ra-input"
                      required
                      autoComplete="name"
                      autoCapitalize="words"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onInput={(e) => setName(e.target.value)}
                      placeholder="Jean Tremblay"
                      data-testid="auth-name-input"
                    />
                  </div>
                )}
                <div>
                  <label className="ra-label"><Mail size={12} className="inline mr-1" />Courriel</label>
                  <input
                    ref={emailRef}
                    className="ra-input"
                    type="email"
                    required
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onInput={(e) => setEmail(e.target.value)}
                    placeholder="vous@exemple.ca"
                    data-testid="auth-email-input"
                  />
                </div>
                {mode !== "forgot" && (
                  <div>
                    <label className="ra-label"><Lock size={12} className="inline mr-1" />Mot de passe</label>
                    <input
                      ref={passwordRef}
                      className="ra-input"
                      type="password"
                      required
                      minLength={8}
                      autoComplete={mode === "register" ? "new-password" : "current-password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onInput={(e) => setPassword(e.target.value)}
                      placeholder="Au moins 8 caractères"
                      data-testid="auth-password-input"
                    />
                  </div>
                )}
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-3" data-testid="auth-submit-button">
                  {loading ? "..." :
                    mode === "login" ? <>Se connecter <ArrowRight size={14} /></> :
                    mode === "register" ? <>Créer le compte <ArrowRight size={14} /></> :
                    <>Envoyer le lien</>}
                </button>
              </form>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs">
              {mode === "login" && (
                <>
                  <button type="button" className="text-[#002FA7] hover:underline" onClick={() => setMode("forgot")} data-testid="switch-to-forgot">
                    Mot de passe oublié ?
                  </button>
                  <button type="button" className="text-gray-600 hover:underline" onClick={() => setMode("register")} data-testid="switch-to-register">
                    Créer un compte →
                  </button>
                </>
              )}
              {mode === "register" && (
                <button type="button" className="text-gray-600 hover:underline" onClick={() => setMode("login")} data-testid="switch-to-login">
                  ← J'ai déjà un compte
                </button>
              )}
              {mode === "forgot" && !forgotSent && (
                <button type="button" className="text-gray-600 hover:underline" onClick={() => setMode("login")}>
                  ← Retour
                </button>
              )}
            </div>

            <p className="text-xs text-gray-400 mt-6">
              Le premier compte créé obtient automatiquement les droits d'administration.
            </p>
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-6">© Riva-Avir Expense Manager</p>
      </div>

      <div className="hidden lg:block relative">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bg})` }} />
        <div className="absolute inset-0 bg-white/30 backdrop-blur-sm" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="bg-white border-2 border-[#0B0F19] p-6 max-w-md">
            <div className="overline mb-2">Réalité des comptables</div>
            <div className="font-heading text-2xl font-bold tracking-tight leading-tight">
              « 4 heures par mois à classer des factures. Maintenant: 4 minutes. »
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
